"use client";
import { Container, Button } from "@mui/material";
import {
	Avatar,
	ListItemIcon,
	ListItemText,
} from "@mui/material";
import type React from "react";
import {
	Box,
	AppBar,
	Toolbar,
	Stack,
	IconButton,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import {
	IconBinaryTreeFilled,
	IconCode,
	IconDeviceTv,
	IconHome,
	IconLabelFilled,
	IconLogout,
	IconMovie,
	IconLink,
	IconPlayerPlay,
} from "@tabler/icons-react";
import { Drawer } from "@mui/material";

import { ListItem, List, useTheme, ListItemButton } from "@mui/material";
import {
	IconUserPlus,
} from "@tabler/icons-react";
import { ListSubheader, styled, type Theme } from "@mui/material";
import { uniqueId } from "lodash";
import Logo from "../player/components/Logo";
import { useAuthenticatedUser } from "./firebase";

const PREFIX = "/dashboard";

const Menuitems = [
	{
		id: uniqueId(),
		title: "Home",
		icon: IconHome,
		href: "/home",
	},
	{
		navlabel: true,
		subheader: "Estrutura",
	},
	{
		id: uniqueId(),
		title: "Grupos",
		icon: IconBinaryTreeFilled,
		href: "/groups",
	},
	{
		id: uniqueId(),
		title: "Categorias",
		icon: IconLabelFilled,
		href: "/categories",
	},
	{
		id: uniqueId(),
		title: "Players",
		icon: IconDeviceTv,
		href: "/players",
	},
	{
		navlabel: true,
		subheader: "Conteúdo",
	},
	{
		id: uniqueId(),
		title: "Vídeos",
		icon: IconMovie,
		href: "/videos",
	},
	{
		id: uniqueId(),
		title: "Links",
		icon: IconLink,
		href: "/links",
	},
	{
		id: uniqueId(),
		title: "Embed",
		icon: IconCode,
		href: "/embed",
	},
	{
		navlabel: true,
		subheader: "Acessos",
	},
	{
		id: uniqueId(),
		title: "Usuários",
		icon: IconUserPlus,
		href: "/users",
	},
];

const SidebarItems = ({ toggleMobileSidebar }: any) => {
	const location = useLocation();
	const pathDirect = location.pathname;

	return (
		<Box sx={{ px: 3 }}>
			<List sx={{ pt: 0 }} className="sidebarNav" component="div">
				{Menuitems.map((item) => {
					if (item.subheader) {
						return <NavGroup item={item} key={item.subheader} />;
					} 

					return (
						<NavItem
							item={item}
							prefix={PREFIX}
							key={item.id}
							pathDirect={pathDirect}
							onClick={toggleMobileSidebar}
						/>
					);
				})}
			</List>
		</Box>
	);
};


const CustomSidebar = () => {

	const sidebarWidth = "270px";

	// Custom CSS for short scrollbar
	const scrollbarStyles = {
		"&::-webkit-scrollbar": {
			width: "7px",
		},
		"&::-webkit-scrollbar-thumb": {
			backgroundColor: "#eff2f7",
			borderRadius: "15px",
		},
	};

	return (
		<Box
			sx={{
				width: sidebarWidth,
				flexShrink: 0,
			}}
		>
			<Drawer
				anchor="left"
				open={true}
				variant="permanent"
				PaperProps={{
					sx: {
						boxSizing: "border-box",
						...scrollbarStyles,
					},
				}}
			>
				<Box
					sx={{
						height: "100%",
						width: "270px",
					}}
				>
					<Logo />
					<Box>
						<SidebarItems />
					</Box>
				</Box>
			</Drawer>
		</Box>
	);
};

type NavGroup = {
	navlabel?: boolean;
	subheader?: string;
};

interface NavGroupProps {
	item: NavGroup;
}

const NavGroup = ({ item }: NavGroupProps) => {
	const ListSubheaderStyle = styled((props: Theme | any) => (
		<ListSubheader disableSticky {...props} />
	))(({ theme }) => ({
		...theme.typography.overline,
		fontWeight: "700",
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(0),
		color: theme.palette.text.primary,
		lineHeight: "26px",
		padding: "3px 12px",
	}));
	return <ListSubheaderStyle>{item.subheader}</ListSubheaderStyle>;
};

type NavGroupType = {
	[x: string]: any;
	id?: string;
	navlabel?: boolean;
	subheader?: string;
	title?: string;
	icon?: any;
	href?: any;
	onClick?: React.MouseEvent<HTMLButtonElement, MouseEvent>;
};

interface NavItemType {
	item: NavGroupType;
	onClick: (event: React.MouseEvent<HTMLElement>) => void;
	prefix?: string;
	hideMenu?: any;
	level?: number | any;
	pathDirect: string;
}

const NavItem = ({
	item,
	level,
	pathDirect,
	onClick,
	prefix = "/",
}: NavItemType) => {
	const Icon = item.icon;
	const theme = useTheme();
	const itemIcon = <Icon stroke={1.5} size="1.3rem" />;
	const finalUrl = `${prefix}${item.href}`;
	const ListItemStyled = styled(ListItem)(() => ({
		padding: 0,
		".MuiButtonBase-root": {
			whiteSpace: "nowrap",
			marginBottom: "2px",
			padding: "8px 10px",
			borderRadius: "8px",
			backgroundColor: level > 1 ? "transparent !important" : "inherit",
			color: theme.palette.text.secondary,
			paddingLeft: "10px",
			"&:hover": {
				backgroundColor: theme.palette.primary.light,
				color: theme.palette.primary.main,
			},
			"&.Mui-selected": {
				color: "white",
				backgroundColor: theme.palette.primary.main,
				"&:hover": {
					backgroundColor: theme.palette.primary.main,
					color: "white",
				},
			},
		},
	}));

	return (
		<List component="div" disablePadding key={item.id}>
			<ListItemStyled>
				<ListItemButton
					component={Link}
					to={finalUrl}
					disabled={item.disabled}
					selected={pathDirect === finalUrl}
					target={item.external ? "_blank" : ""}
					onClick={onClick}
					key={item.id}
				>
					<ListItemIcon
						sx={{
							minWidth: "36px",
							p: "3px 0",
							color: "inherit",
						}}
					>
						{itemIcon}
					</ListItemIcon>
					<ListItemText>
						<>{item.title}</>
					</ListItemText>
				</ListItemButton>
			</ListItemStyled>
		</List>
	);
};

export const Header = () => {	
	const authenticatedUser = useAuthenticatedUser();

	const AppBarStyled = styled(AppBar)(({ theme }) => ({
		boxShadow: "none",
		background: theme.palette.background.paper,
		justifyContent: "center",
		backdropFilter: "blur(4px)",
		[theme.breakpoints.up("lg")]: {
			minHeight: "70px",
		},
	}));
	const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
		width: "100%",
		color: theme.palette.text.secondary,
	}));

	return (
		<AppBarStyled position="sticky" color="default">
			<ToolbarStyled>

				<Box flexGrow={1} />
				<Stack spacing={1} direction="row" alignItems="center">
					<Button
						component={Link}
						to="/player"
						variant="contained"
						startIcon={<IconPlayerPlay width={20} height={20} />}
						title="Ir para Player"
						sx={{
							backgroundColor: '#1976d2',
							color: 'white',
							'&:hover': {
								backgroundColor: '#1565c0',
							},
							minWidth: 'auto',
							padding: '8px 16px',
						}}
					>
						Ir para Player
					</Button>
					<IconButton onClick={authenticatedUser.logout}>
						<IconLogout width={20} height={20} />
					</IconButton>
					<IconButton size="large" color="inherit">
						<Avatar
							src={authenticatedUser.user?.photoURL || ""}
							alt={authenticatedUser.user?.displayName || ""}
							sx={{
								width: 35,
								height: 35,
							}}
						/>
					</IconButton>
				</Stack>
			</ToolbarStyled>
		</AppBarStyled>
	);
};

const MainWrapper = styled("div")(() => ({
	display: "flex",
	minHeight: "100vh",
	width: "100%",
}));

const PageWrapper = styled("div")(() => ({
	display: "flex",
	flexGrow: 1,
	paddingBottom: "60px",
	flexDirection: "column",
	zIndex: 1,
	backgroundColor: "transparent",
}));

export function RootLayout({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<MainWrapper className="mainwrapper">
			{/* ------------------------------------------- */}
			{/* Sidebar */}
			{/* ------------------------------------------- */}
			<CustomSidebar />
			{/* ------------------------------------------- */}
			{/* Main Wrapper */}
			{/* ------------------------------------------- */}
			<PageWrapper className="page-wrapper">
				{/* ------------------------------------------- */}
				{/* Header */}
				{/* ------------------------------------------- */}
				<Header />
				{/* ------------------------------------------- */}
				{/* PageContent */}
				{/* ------------------------------------------- */}
				<Container
					sx={{
						paddingTop: "20px",
						maxWidth: "1200px",
					}}
				>
					{/* ------------------------------------------- */}
					{/* Page Route */}
					{/* ------------------------------------------- */}
					<Box sx={{ minHeight: "calc(100vh - 170px)" }}>{children}</Box>
					{/* ------------------------------------------- */}
					{/* End Page */}
					{/* ------------------------------------------- */}
				</Container>
			</PageWrapper>
		</MainWrapper>
	);
} 
